import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Info,
  AlertTriangle,
  Compass,
  Sparkles,
  AlertOctagon,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import CategoryPageShell from "../../../components/workspace/CategoryPageShell";
import { Para, H4, Bullets, Callout, DriveLink } from "../../../components/workspace/HubText";
import { useHashAccordion } from "../../../hooks/useHashAccordion";
import {
  TRAINING_STEPS,
  CORE_TRAINING,
  PROVIDERS,
  STARTER_PATH,
  CPD_TRACKER_HREF,
  BUNDLES,
} from "../../../data/proCarerCircleContent";

export default function TrainingCpd() {
  const [openBundle, setOpenBundle] = useState("");
  useHashAccordion(BUNDLES.map((b) => b.id), setOpenBundle);

  return (
    <CategoryPageShell
      testId="pcc-category-training-cpd"
      slug="training-cpd"
      eyebrow="Training & CPD"
      title="Bundles, providers and a clear path."
      description="JCC does not deliver training. The providers listed below are independent, accredited organisations."
    >
      {/* How training works */}
      <section className="min-w-0">
        <h3 className="font-display text-xl font-semibold mb-3">How training works</h3>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {TRAINING_STEPS.map((s) => (
            <li key={s.n} className="card-base p-5 flex flex-col min-w-0">
              <div className="w-9 h-9 rounded-full bg-teal text-white flex items-center justify-center font-semibold text-sm">{s.n}</div>
              <h4 className="font-display text-base font-semibold mt-4">{s.title}</h4>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Core training + why it matters */}
      <section className="min-w-0">
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="card-base p-6 min-w-0">
            <h3 className="font-display text-lg font-semibold">Core training commonly expected</h3>
            <Bullets color="text-teal" items={CORE_TRAINING} />
          </div>
          <div className="card-base p-6 lg:col-span-2 space-y-4 min-w-0">
            <div>
              <div className="flex items-center gap-2 text-teal font-semibold"><Info className="w-4 h-4" strokeWidth={2} /> Why this matters</div>
              <Para>Families may ask about training before confirming a booking, or later if care needs change. Having certificates ready helps build trust quickly, avoid awkward conversations, support Direct Payments requirements and protect you professionally.</Para>
            </div>
            <div>
              <div className="flex items-center gap-2 text-amber-700 font-semibold"><AlertTriangle className="w-4 h-4" strokeWidth={2} /> An important note</div>
              <Para>Training does not replace experience — but it does show commitment to safe practice and ongoing professional development. Some clients may require additional or specialist training depending on their needs.</Para>
            </div>
            <div>
              <div className="flex items-center gap-2 text-ink font-semibold"><Compass className="w-4 h-4" strokeWidth={2} /> Who decides what training you need?</div>
              <Para>Requirements are agreed between you, the client or family, and any relevant funding body or insurer. JCC cannot determine individual training requirements but can guide you to trusted accredited providers.</Para>
            </div>
          </div>
        </div>
      </section>

      {/* Providers */}
      <section id="training-providers" className="scroll-mt-24 min-w-0">
        <h3 className="font-display text-xl font-semibold mb-3">Recommended training providers</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-3">
          {PROVIDERS.map((p) => (
            <article key={p.name} className="card-base p-6 min-w-0" data-testid={`provider-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <h4 className="font-display text-lg font-semibold">{p.name}</h4>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">{p.body}</p>
            </article>
          ))}
        </div>
        <Callout tone="amber" title="Important" icon={AlertTriangle}>
          JCC provides information only. All training is completed directly with the provider, who is responsible for course content, certification and accreditation.
        </Callout>
      </section>

      {/* Starter path */}
      <section id="starter-path" className="scroll-mt-24 min-w-0">
        <h3 className="font-display text-xl font-semibold mb-3">New Carer Starter Path</h3>
        <ol className="space-y-4 mb-3">
          {STARTER_PATH.map((s) => (
            <li key={s.n} className="card-base p-5 md:p-6 flex gap-4 items-start min-w-0">
              <div className="w-9 h-9 rounded-full bg-purple text-white flex items-center justify-center font-semibold text-sm shrink-0">{s.n}</div>
              <div className="min-w-0">
                <h4 className="font-display text-base md:text-lg font-semibold">{s.title}</h4>
                <p className="mt-1.5 text-sm md:text-base text-ink-muted leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <Callout tone="purple" title="Reassurance for new carers">
          Feeling unsure does not mean you are unprepared. Most good carers build their confidence through experience, reflection and ongoing learning. Training supports your practice — it does not define your worth as a carer.
        </Callout>
      </section>

      {/* Bundles accordion */}
      <section id="bundles" className="scroll-mt-24 min-w-0">
        <h3 className="font-display text-xl font-semibold mb-3">Recommended Bundles</h3>
        <Accordion type="single" collapsible value={openBundle} onValueChange={setOpenBundle} className="space-y-3" data-testid="bundles-accordion">
          {BUNDLES.map((b) => (
            <AccordionItem key={b.id} value={b.id} id={b.id} className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24 min-w-0">
              <AccordionTrigger className="py-5 hover:no-underline">
                <span className="flex items-center gap-3 text-left">
                  <Sparkles className="w-5 h-5 text-teal shrink-0" strokeWidth={1.75} />
                  <span className="flex flex-col">
                    <span className="font-display text-lg font-semibold">{b.title}</span>
                    <span className="text-sm text-ink-muted font-normal">{b.blurb}</span>
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6 pt-1 text-base leading-relaxed">
                <H4>Recommended modules</H4>
                <Bullets color="text-teal" items={b.courses} />
                <H4>Estimated total cost (UK online pricing, approx. 2026)</H4>
                <Bullets color="text-teal" items={b.costs} />
                <Callout tone="slate" title="Note" icon={Info}>{b.note}</Callout>
                <H4>&ldquo;Minimum safe to start&rdquo; checklist</H4>
                <Bullets color="text-teal" items={b.checklist} />
                <div className="mt-6">
                  <DriveLink href={CPD_TRACKER_HREF} label="Download CPD Tracker" variant="download" testId={`bundle-cpd-${b.id}`} />
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Certificate organisation & families ask for */}
      <section className="min-w-0">
        <div className="grid lg:grid-cols-2 gap-5">
          <div id="certificate-organisation" className="card-base p-6 scroll-mt-24 min-w-0" data-testid="cert-organisation">
            <h3 className="font-display text-lg font-semibold">Certificate organisation guidance</h3>
            <Para>Professional carers are often asked to provide proof of training at short notice. Being organised builds credibility, speeds up placement approvals, protects you in disputes and reduces stress.</Para>
            <H4>Step 1 — Create a digital folder system</H4>
            <Para>📁 Training Certificates → Foundation · Live-In · Specialist · Professional Growth · Expired/Archived.</Para>
            <H4>Step 2 — Rename every certificate clearly</H4>
            <Para>e.g. <em>Medication_Administration_2026.pdf</em>, <em>Manual_Handling_Practical_2026.pdf</em>.</Para>
            <H4>Step 3 — Track expiry dates</H4>
            <Para>
              Enter it into your{" "}
              <Link
                to={CPD_TRACKER_HREF}
                className="text-purple font-semibold underline"
                data-testid="cert-organisation-cpd-tracker-link"
              >
                CPD Tracker
              </Link>
              , add the expiry date, and set a calendar reminder 1–2 months before.
            </Para>
            <H4>Step 4 — Keep a &ldquo;Ready to Send&rdquo; pack</H4>
            <Para>📁 Placement Ready Pack: most recent CV, insurance certificate, DBS (if applicable), core and specialist certificates.</Para>
            <H4>Step 5 — Review annually</H4>
            <Para>Archive expired certificates, update planner, identify gaps, refresh anything overdue.</Para>
            <Callout tone="amber" title="What to avoid" icon={AlertOctagon}>
              Storing certificates only in email · relying on WhatsApp downloads · mixing personal and professional documents · letting expiry dates lapse unnoticed.
            </Callout>
          </div>
          <div className="card-base p-6 min-w-0" data-testid="families-ask">
            <h3 className="font-display text-lg font-semibold">What families usually ask for</h3>
            <Para>Families don’t usually ask for “bundles” or named courses. They ask for reassurance. Here’s what they commonly want to see.</Para>
            <H4>Proof of training</H4>
            <Para>Safeguarding · Manual handling · First aid · Medication awareness · Dementia training (if relevant).</Para>
            <H4>DBS (if UK-based)</H4>
            <Para>Many families request an Enhanced DBS check. Even in private arrangements, having a current DBS builds immediate trust and supports safeguarding best practice.</Para>
            <H4>Insurance</H4>
            <Para>Professional indemnity and public liability insurance are often expected for independent carers.</Para>
            <H4>Experience with specific conditions</H4>
            <Para>Confidence comes from preparation — not exaggeration.</Para>
            <H4>Documentation &amp; reporting</H4>
            <Para>Some families expect daily care notes, incident reporting, medication logs. Clear communication early prevents confusion later.</Para>
            <H4>Availability &amp; sleep expectations</H4>
            <Para>This is where your boundaries matter. Training protects competence — agreements protect sustainability.</Para>
          </div>
        </div>
      </section>

      <Callout tone="slate" title="Legal & professional disclaimer" icon={Info}>
        The training guidance and pathway suggestions within Just Care Community are provided for general informational purposes only. They do not replace formal accredited qualifications where required, do not constitute legal, clinical or regulatory advice, do not guarantee suitability for specific placements, and do not override employer, agency or local authority requirements. Individual carers are responsible for ensuring training meets the requirements of the roles they accept and that they operate within their competence. JCC does not provide regulated training services and is not a regulatory body.
      </Callout>
    </CategoryPageShell>
  );
}
